import { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * A tiny combobox for the "项目" field on the quote edit dialog.
 *
 *   • Free-text: type any new project name → it gets committed as-is on select.
 *   • Existing: shown in the list, filtered by the search input.
 *   • "New" hint: when the query doesn't match any existing project, an explicit
 *     "+ 新建 «query»" row appears so users know they can create it right here.
 *
 * Props:
 *   • value       — current project string (empty for "unset")
 *   • onChange    — (string) => void
 *   • options     — list of existing project labels the current user can pick
 *   • placeholder — string, shown on the trigger button when value is empty
 *   • disabled    — bool
 *   • testId      — data-testid prefix (defaults to "project-combo")
 */
export default function ProjectCombobox({
  value,
  onChange,
  options = [],
  placeholder = "",
  disabled = false,
  testId = "project-combo",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const q = query.trim();
  const exists = options.some((o) => o.toLowerCase() === q.toLowerCase());
  const showCreate = q.length > 0 && !exists;

  const commit = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className={cn("w-full justify-between rounded-md font-normal", !value && "text-muted-foreground")}
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" data-testid={`${testId}-popover`}>
        <Command shouldFilter={true}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={placeholder}
            data-testid={`${testId}-input`}
          />
          <CommandList>
            <CommandEmpty>
              {showCreate ? null : <span className="text-xs text-muted-foreground">-</span>}
            </CommandEmpty>
            {options.length > 0 && (
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt}
                    value={opt}
                    onSelect={() => commit(opt)}
                    data-testid={`${testId}-option-${opt}`}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === opt ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{opt}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showCreate && (
              <CommandGroup heading=" ">
                <CommandItem
                  value={`__create__${q}`}
                  onSelect={() => commit(q)}
                  data-testid={`${testId}-create-option`}
                  className="text-brand"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span className="truncate">新建 &laquo;{q}&raquo;</span>
                </CommandItem>
              </CommandGroup>
            )}
            {value && (
              <CommandGroup heading=" ">
                <CommandItem
                  value="__clear__"
                  onSelect={() => commit("")}
                  data-testid={`${testId}-clear-option`}
                  className="text-muted-foreground"
                >
                  <span className="ml-6 truncate">- 清除</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
